import { useState, useRef, useCallback } from 'react';
import { Shape } from '../types/Shape';

export interface UseCanvasSelectionReturn {
  // Selection state
  selectedShapes: string[];
  setSelectedShapes: React.Dispatch<React.SetStateAction<string[]>>;
  showResizeHandles: boolean;
  setShowResizeHandles: React.Dispatch<React.SetStateAction<boolean>>;

  // Lasso selection state
  isLassoSelecting: boolean;
  setIsLassoSelecting: React.Dispatch<React.SetStateAction<boolean>>;
  lassoStart: { x: number; y: number } | null;
  setLassoStart: React.Dispatch<React.SetStateAction<{ x: number; y: number } | null>>;
  lassoEnd: { x: number; y: number } | null;
  setLassoEnd: React.Dispatch<React.SetStateAction<{ x: number; y: number } | null>>;

  // Copy/Paste state
  copiedShapes: Shape[];
  setCopiedShapes: React.Dispatch<React.SetStateAction<Shape[]>>;

  // Context menu state
  contextMenu: { x: number; y: number; shapeId: string } | null;
  setContextMenu: React.Dispatch<React.SetStateAction<{ x: number; y: number; shapeId: string } | null>>;

  // Dragging state
  draggingShapeId: string | null;
  setDraggingShapeId: React.Dispatch<React.SetStateAction<string | null>>;

  // Refs
  selectedShapesStartPositionsRef: React.MutableRefObject<Map<string, { x: number; y: number }>>;

  // Utility functions
  clearSelection: () => void;
  selectShape: (shapeId: string, addToSelection?: boolean) => void;
  selectAll: (shapes: Shape[]) => void;
  isShapeSelected: (shapeId: string) => boolean;
}

export function useCanvasSelection(): UseCanvasSelectionReturn {
  // Selection state
  const [selectedShapes, setSelectedShapes] = useState<string[]>([]);
  const [showResizeHandles, setShowResizeHandles] = useState(false);

  // Lasso selection state
  const [isLassoSelecting, setIsLassoSelecting] = useState(false);
  const [lassoStart, setLassoStart] = useState<{ x: number; y: number } | null>(null);
  const [lassoEnd, setLassoEnd] = useState<{ x: number; y: number } | null>(null);

  // Copy/Paste state
  const [copiedShapes, setCopiedShapes] = useState<Shape[]>([]);

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; shapeId: string } | null>(null);

  // Dragging state
  const [draggingShapeId, setDraggingShapeId] = useState<string | null>(null);

  // Refs
  const selectedShapesStartPositionsRef = useRef<Map<string, { x: number; y: number }>>(new Map());

  // Clear all selections
  const clearSelection = useCallback(() => {
    setSelectedShapes([]);
    setShowResizeHandles(false);
  }, []);

  // Select a shape (optionally add to current selection)
  const selectShape = useCallback((shapeId: string, addToSelection = false) => {
    if (addToSelection) {
      setSelectedShapes(prev => {
        if (prev.includes(shapeId)) {
          // Deselect if already selected
          return prev.filter(id => id !== shapeId);
        }
        return [...prev, shapeId];
      });
    } else {
      setSelectedShapes([shapeId]);
    }
    setShowResizeHandles(true);
  }, []);

  // Select all shapes
  const selectAll = useCallback((shapes: Shape[]) => {
    setSelectedShapes(shapes.map(s => s.id));
    setShowResizeHandles(true);
  }, []);

  // Check if a shape is selected
  const isShapeSelected = useCallback((shapeId: string) => {
    return selectedShapes.includes(shapeId);
  }, [selectedShapes]);

  return {
    // Selection state
    selectedShapes,
    setSelectedShapes,
    showResizeHandles,
    setShowResizeHandles,

    // Lasso selection state
    isLassoSelecting,
    setIsLassoSelecting,
    lassoStart,
    setLassoStart,
    lassoEnd,
    setLassoEnd,

    // Copy/Paste state
    copiedShapes,
    setCopiedShapes,

    // Context menu state
    contextMenu,
    setContextMenu,

    // Dragging state
    draggingShapeId,
    setDraggingShapeId,

    // Refs
    selectedShapesStartPositionsRef,

    // Utility functions
    clearSelection,
    selectShape,
    selectAll,
    isShapeSelected,
  };
}
